import type { ReactNode } from "react";

import { Trans } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { AlertTriangle, Loader2, RotateCw } from "lucide-react";

import { Button } from "./button";

interface LoadErrorStateProps {
  /** What failed, e.g. t`Could not load your appointments.` */
  title: ReactNode;
  /** Optional second line (what the user can do). */
  description?: ReactNode;
  /** Re-runs the failed load. Omit to render the card without a Retry. */
  onRetry?: () => void;
  /** Shows a spinner on Retry while the reload is in flight. */
  retrying?: boolean;
  /** Extra action next to Retry (e.g. a link back to the dashboard). */
  secondary?: ReactNode;
  className?: string;
}

/**
 * The bordered "could not load" card with Retry (P16). A failed load must
 * never fall through to an empty state — "no appointments" and "we couldn't
 * reach the server" are different answers. Pattern lifted from
 * PatientRequestsPanel.
 */
export function LoadErrorState({
  title,
  description,
  onRetry,
  retrying = false,
  secondary,
  className,
}: LoadErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "border-destructive/40 bg-destructive/5 flex flex-col items-center rounded-xl border p-6 text-center",
        className,
      )}
    >
      <AlertTriangle className="text-destructive size-6" aria-hidden />
      <p className="text-destructive mt-2 text-sm font-medium">{title}</p>
      {description ? <p className="text-muted-foreground mt-1 text-sm">{description}</p> : null}
      {onRetry || secondary ? (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {onRetry ? (
            <Button variant="outline" size="touch" onClick={onRetry} disabled={retrying}>
              {retrying ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <RotateCw className="size-4" aria-hidden />
              )}
              <Trans>Retry</Trans>
            </Button>
          ) : null}
          {secondary}
        </div>
      ) : null}
    </div>
  );
}
